import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Check, Coins, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { premiumApi } from '../lib/api';

export const PremiumClubCard = ({ user, isItalian, isCaptain, onActivated }) => {
  const [info, setInfo] = useState(null);
  const [buying, setBuying] = useState(false);
  const [confirmCoins, setConfirmCoins] = useState(false);

  const loadInfo = async () => {
    try {
      const res = await premiumApi.info(user.club_id);
      setInfo(res.data);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (user?.club_id) loadInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.club_id]);

  if (!user?.club_id || !info) return null;

  const perks = isItalian ? info.perks : info.perks;

  const handleStripe = async () => {
    setBuying(true);
    try {
      const res = await premiumApi.checkout({ club_id: user.club_id, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
      setBuying(false);
    }
  };

  const handleCoins = async () => {
    if (!confirmCoins) {
      setConfirmCoins(true);
      setTimeout(() => setConfirmCoins(false), 5000);
      return;
    }
    setConfirmCoins(false);
    setBuying(true);
    try {
      const res = await premiumApi.buyWithCoins(user.club_id);
      toast.success(res.data.message);
      await loadInfo();
      if (onActivated) onActivated(info.price_coins);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setBuying(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="max-w-2xl mx-auto bg-gradient-to-br from-[#1a1407] to-[#0a0d15] border border-[#FFD700]/30 p-6 relative overflow-hidden"
      data-testid="premium-club-card"
    >
      <div className="absolute top-0 right-0 w-40 h-40 bg-[#FFD700]/5 rounded-full blur-3xl" />
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center">
          <Crown size={24} className="text-[#FFD700]" />
        </div>
        <div>
          <h2 className="font-heading text-lg md:text-xl font-bold uppercase tracking-wider text-[#FFD700]">
            {isItalian ? 'Club Premium' : 'Premium Club'}
          </h2>
          <p className="text-xs text-slate-400">
            {isItalian ? `Stagione ${info.season || ''} — ${info.club_name}` : `Season — ${info.club_name}`}
          </p>
        </div>
        {info.active && (
          <span className="ml-auto px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#FFD700]/15 border border-[#FFD700]/50 text-[#FFD700] rounded-full" data-testid="premium-active-badge">
            {isItalian ? 'Attivo' : 'Active'} 👑
          </span>
        )}
      </div>

      <ul className="space-y-2 mb-5">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
            <Check size={16} className="text-[#FFD700] mt-0.5 flex-shrink-0" />
            {p}
          </li>
        ))}
      </ul>

      {!info.active && isCaptain && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            onClick={handleStripe}
            disabled={buying}
            className="bg-[#FFD700] text-black hover:bg-[#FFC400] font-bold"
            data-testid="premium-stripe-btn"
          >
            {buying ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CreditCard size={16} className="mr-2" />}
            {isItalian ? `Carta — €${info.price_eur.toFixed(2).replace('.', ',')} / stagione` : `Card — €${info.price_eur}`}
          </Button>
          <Button
            onClick={handleCoins}
            disabled={buying || info.club_coins < info.price_coins}
            variant="outline"
            className={`border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 ${confirmCoins ? 'bg-[#FFD700]/15 border-[#FFD700]' : ''}`}
            data-testid="premium-coins-btn"
          >
            {buying ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Coins size={16} className="mr-2" />}
            {confirmCoins
              ? (isItalian ? 'Confermi? Clicca di nuovo' : 'Confirm? Click again')
              : `${info.price_coins.toLocaleString()} coins`}
          </Button>
          {info.club_coins < info.price_coins && (
            <p className="text-[10px] text-slate-500 sm:col-span-2">
              {isItalian
                ? `Saldo club: ${info.club_coins.toLocaleString()} coins — insufficiente per pagare con coins`
                : `Club balance: ${info.club_coins.toLocaleString()} coins — not enough`}
            </p>
          )}
        </div>
      )}
      {!info.active && !isCaptain && (
        <p className="text-xs text-slate-500">
          {isItalian ? 'Solo il capitano del club può attivare il Premium.' : 'Only the club captain can activate Premium.'}
        </p>
      )}
    </motion.div>
  );
};
